import type { LngLat } from './types.ts';
import { distanceM } from './geo.ts';
import { routeAlongPaths, type Graph } from './routing.ts';
import { toSteps, routeLength, type Step } from './maneuvers.ts';
import { API_BASE } from './api.ts';

/**
 * One route across two networks.
 *
 * A buggy at Sanctuary Cove does not stay on the golf course. It comes off the
 * eighteenth, through the gate, along the estate roads, and parks at the
 * village — the CruiseCart trip. No navigation app joins those two networks:
 * Apple Maps and Waze know the estate roads and stop at the course boundary,
 * and every golf app knows the cart paths and stops at the gate.
 *
 * This is the join. The cart-path leg comes from the app's own Dijkstra over
 * OpenStreetMap `golf=cartpath` ways; the estate leg comes from Valhalla via
 * the worker, with private-access routing enabled because the rider is already
 * inside the gate. Stitched, they produce a single line and a single set of
 * turn-by-turn instructions.
 *
 * That continuity is also the case for the CarPlay navigation entitlement: the
 * product is not a golf app asking to draw a map, it is navigation over a
 * superset of the road network.
 */

export type TravelMode = 'walk' | 'drive';

export interface JourneyLeg {
  line: LngLat[];
  distanceM: number;
  /** Which network this part of the trip runs on. */
  network: 'cart-path' | 'estate';
  /** True when the leg is a straight line because routing failed. */
  approximate?: boolean;
}

export interface Journey {
  line: LngLat[];
  legs: JourneyLeg[];
  steps: Step[];
  distanceM: number;
  /** Seconds, from the router where known and from a walking/driving pace where not. */
  durationS: number;
}

/** Buggy pace and walking pace, metres per second. Used only where the router
 *  gives no time of its own — a cart-path leg has no external estimate. */
const PACE_MS: Record<TravelMode, number> = { walk: 1.3, drive: 4.2 };

interface RemoteLeg {
  line: [number, number][];
  distanceM: number;
  durationS: number;
  source: string;
}

async function estateLeg(
  from: LngLat,
  to: LngLat,
  mode: TravelMode
): Promise<RemoteLeg | null> {
  const q = new URLSearchParams({
    fromLng: String(from[0]),
    fromLat: String(from[1]),
    toLng: String(to[0]),
    toLat: String(to[1]),
    mode,
  });
  try {
    const r = await fetch(`${API_BASE}/api/route?${q}`);
    if (!r.ok) return null;
    return (await r.json()) as RemoteLeg;
  } catch {
    return null;
  }
}

/**
 * Where the cart network gives way to the estate.
 *
 * The handover point is the vertex of the cart network nearest the
 * destination — the place a rider would naturally leave the paths. Picking the
 * nearest point to the *start* instead would route them backwards to an exit
 * behind them, which is the obvious wrong answer and the easy one to write.
 */
function handover(graph: Graph, towards: LngLat): LngLat | null {
  let best: LngLat | null = null;
  let bestD = Infinity;
  for (const n of graph.nodes) {
    const d = distanceM(n, towards);
    if (d < bestD) { best = n; bestD = d; }
  }
  return best;
}

/** How close to the destination the cart network has to reach to be worth using. */
const CART_USEFUL_WITHIN_M = 2500;

export interface PlanOptions {
  mode?: TravelMode;
  unit?: 'm' | 'yd';
  destination?: string;
}

/**
 * Plan a trip from `from` to `to`, using cart paths where they help and the
 * estate network for the rest.
 */
export async function planJourney(
  graph: Graph,
  from: LngLat,
  to: LngLat,
  opts: PlanOptions = {}
): Promise<Journey> {
  const mode = opts.mode ?? 'drive';
  const legs: JourneyLeg[] = [];

  const exit = graph.nodes.length ? handover(graph, to) : null;
  // Only route over cart paths if they actually get you closer. On a trip that
  // starts and ends off the course they are a detour, and a rider sent onto a
  // fairway to reach the village would rightly never trust it again.
  const cartHelps =
    exit != null &&
    distanceM(exit, to) < distanceM(from, to) &&
    distanceM(exit, to) < CART_USEFUL_WITHIN_M;

  let cursor = from;
  if (cartHelps && exit) {
    const cart = routeAlongPaths(graph, from, exit);
    if (cart.ok && cart.path.length >= 2) {
      legs.push({ line: cart.path, distanceM: cart.distanceM, network: 'cart-path' });
      cursor = cart.path[cart.path.length - 1];
    }
  }

  if (distanceM(cursor, to) > 15) {
    const remote = await estateLeg(cursor, to, mode);
    if (remote && remote.line.length >= 2) {
      legs.push({
        line: remote.line as LngLat[],
        distanceM: remote.distanceM || Math.round(routeLength(remote.line as LngLat[])),
        network: 'estate',
        approximate: remote.source === 'straight-line',
      });
    } else {
      legs.push({
        line: [cursor, to],
        distanceM: Math.round(distanceM(cursor, to)),
        network: 'estate',
        approximate: true,
      });
    }
  }

  // Join, dropping the duplicated vertex where one leg ends and the next
  // begins — left in, it reads as a zero-length segment and the maneuver
  // generator would see a meaningless bearing at the seam.
  const line: LngLat[] = [];
  for (const leg of legs) {
    for (const p of leg.line) {
      const last = line[line.length - 1];
      if (!last || distanceM(last, p) > 0.5) line.push(p);
    }
  }

  const distance = legs.reduce((s, l) => s + l.distanceM, 0);
  return {
    line,
    legs,
    steps: toSteps(line, { mode, unit: opts.unit, destination: opts.destination }),
    distanceM: distance,
    durationS: Math.round(distance / PACE_MS[mode]),
  };
}
